import { Box, MenuList, Stack } from '@mui/material';
import { Button, Typography, colors } from '@sweep-io/sweep-design';
import { SlackConnectButton } from './SlackConnectButton';
import { SlackLogo2 } from '../../../icons/generalIcons';
import { HubspotConnectButton } from './HubspotConnectButton';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { IntegrationOption } from './IntegrationsOption';
import { SlackIncludeSandboxesCheckbox } from './SlackIncludeSandboxesCheckbox';
import { HubspotNewLogo, MicrosoftTeamsLogo } from '../../common/connect-sweep-to/integrationIcons';
import { useWebhooksContext } from '../webhooks/WebhooksContext';
import { useIntegrations } from '../../../hooks/useIntegrations';
import { useSelector } from 'react-redux';
import {
  selectCustomIntegrations,
  selectSlackIntegrationStatus,
  selectTeamsIntegrationStatus,
  SlackConnectionStatus,
  TeamsConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { CustomIntegrationsOption } from './CustomIntegrationsOption';
import { usePermissions } from '../../../permissions/usePermissions';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import { TeamsConnectButton } from './TeamsConnectButton';
import { useRunOnce } from '../../common/useRunOnce';
import { SearchInput } from '../../common/SearchInput';
import { useCallback, useMemo, useState } from 'react';
import { useHubspot } from '../../pages/canvas-pages/canvas-hubspot-page/useHubspot';
import pluralize from 'pluralize';
import { SingleSelectFilter } from '../../common/filters/single-select-filter/SingleSelectFilter';

const slackHeader = 'Slack';
const hubspotHeader = 'Hubspot';
const microsoftTeamsHeader = 'Microsoft office Teams';

enum StatusFilterValue {
  ALL = 'all',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
}

const statusFilterOptions = [
  { value: StatusFilterValue.ALL, label: 'All' },
  { value: StatusFilterValue.CONNECTED, label: 'Connected' },
  { value: StatusFilterValue.DISCONNECTED, label: 'Disconnected' },
];

export const IntegrationsTab = () => {
  const { setWebhooks } = useWebhooksContext();
  const { getCustomIntegrations } = useIntegrations();
  const customIntegrations = useSelector(selectCustomIntegrations);
  const { evaluatePermissions: checkPermissions } = usePermissions();
  const [isAllowedBtnCreate] = checkPermissions([
    { permission: 'create:data-integration:custom-integrations' },
  ]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(StatusFilterValue.ALL);
  const { multiAccounts } = useFeatureToggle();

  const teamsStatus = useSelector(selectTeamsIntegrationStatus);
  const slackStatus = useSelector(selectSlackIntegrationStatus);
  const { connectedHubspotOrg } = useHubspot();

  const isSlackConnected = slackStatus === SlackConnectionStatus.Connected;
  const isHubspotConnected = !!connectedHubspotOrg;
  const isMicrosoftTeamsConnected = teamsStatus === TeamsConnectionStatus.Connected;

  const customIntegrationsToDisplay = useMemo(() => {
    return customIntegrations.filter((integration) => {
      const matchesSearch = integration.name.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus =
        statusFilter === StatusFilterValue.ALL || statusFilter === StatusFilterValue.CONNECTED;
      return matchesSearch && matchesStatus;
    });
  }, [customIntegrations, searchText, statusFilter]);

  useRunOnce(async () => {
    await getCustomIntegrations();
  });

  const matchesStatusFilter = useCallback(
    (isConnected: boolean) => {
      if (statusFilter === StatusFilterValue.ALL) return true;
      if (statusFilter === StatusFilterValue.CONNECTED) return isConnected;
      return !isConnected;
    },
    [statusFilter],
  );

  const shouldBeDisplayed = useCallback(
    (integrationName: string, isConnected: boolean) => {
      const matchesSearch =
        integrationName.toLowerCase().includes(searchText.toLowerCase()) ||
        searchText.trim() === '';
      return matchesSearch && matchesStatusFilter(isConnected);
    },
    [searchText, matchesStatusFilter],
  );

  const connectedIntegrationsCount = useMemo(() => {
    return (
      (isSlackConnected ? 1 : 0) +
      (isHubspotConnected ? 1 : 0) +
      (isMicrosoftTeamsConnected ? 1 : 0) +
      customIntegrations.length
    );
  }, [customIntegrations, isSlackConnected, isHubspotConnected, isMicrosoftTeamsConnected]);

  const isListEmpty = useMemo(() => {
    return (
      !customIntegrationsToDisplay.length &&
      !shouldBeDisplayed(slackHeader, isSlackConnected) &&
      !shouldBeDisplayed(hubspotHeader, isHubspotConnected) &&
      !shouldBeDisplayed(microsoftTeamsHeader, isMicrosoftTeamsConnected)
    );
  }, [
    customIntegrationsToDisplay,
    shouldBeDisplayed,
    isSlackConnected,
    isHubspotConnected,
    isMicrosoftTeamsConnected,
  ]);

  return (
    <Stack gap="16px">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h3">Integrations</Typography>

        <RestrictedTooltip
          validation={{ type: 'explicit', allowed: isAllowedBtnCreate }}
          notAllowedTitle={'To adjust integration settings, please contact your admin.'}
        >
          <Button
            disabled={!isAllowedBtnCreate}
            onClick={() => setWebhooks(true)}
            size="small"
            startIconName="Plus"
            variant="primary"
          >
            {multiAccounts ? 'Connect new' : 'Add new'}
          </Button>
        </RestrictedTooltip>
      </Box>

      {multiAccounts && (
        <Stack
          sx={{
            backgroundColor: colors.grey[100],
            padding: '8px 8px 8px 16px',
            justifyContent: 'space-between',
            flexDirection: 'row',
            width: '100%',
            borderRadius: '8px',
            alignItems: 'center',
          }}
        >
          <SingleSelectFilter
            filterTitleText="Status"
            options={statusFilterOptions}
            selectedItemValue={statusFilter}
            onSelectItem={(value) => setStatusFilter(value)}
          />
          <Stack width="320px">
            <SearchInput
              placeholder="Search"
              withFixedMagnifyingGlassIcon
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
            />
          </Stack>
        </Stack>
      )}

      <Typography variant="caption" color={colors.grey[700]}>
        {pluralize('connected integration', connectedIntegrationsCount, true)}
      </Typography>

      <MenuList
        sx={{
          '&.MuiList-root': {
            padding: 0,
          },
          '.MuiMenuItem-root': {
            padding: 2,
            gap: 2,
            borderBottom: `1px solid ${colors.grey[120]}`,
            margin: 0,
            minHeight: '80px',
            justifyContent: 'flex-start',
          },
        }}
      >
        {isListEmpty && (
          <Typography variant="body" color={colors.grey[500]}>
            No results found
          </Typography>
        )}

        {shouldBeDisplayed(slackHeader, isSlackConnected) && (
          <IntegrationOption
            name={slackHeader}
            logo={<SlackLogo2 />}
            description="Connect Slack to keep your GTM teams informed with actionable notifications"
            actionButton={<SlackConnectButton />}
            additionalContent={<SlackIncludeSandboxesCheckbox />}
          />
        )}

        {shouldBeDisplayed(hubspotHeader, isHubspotConnected) && (
          <IntegrationOption
            name={hubspotHeader}
            logo={<HubspotNewLogo />}
            description="Connect HubSpot to visualize funnels and manage campaign enrollments"
            actionButton={<HubspotConnectButton />}
          />
        )}

        {shouldBeDisplayed(microsoftTeamsHeader, isMicrosoftTeamsConnected) && (
          <IntegrationOption
            name="Microsoft office Teams"
            logo={<MicrosoftTeamsLogo />}
            description="Connect Teams to keep your GTM teams informed"
            actionButton={<TeamsConnectButton />}
          />
        )}

        {customIntegrationsToDisplay.map((el) => {
          return <CustomIntegrationsOption integration={el} key={el.id} />;
        })}
      </MenuList>
    </Stack>
  );
};
